const env = process.env as Record<string, string | undefined>;

const getDefaultApiUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost:8000/api/v1';
  }

  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${window.location.hostname}:8000/api/v1`;
};

const isLoopbackHost = (host: string) => host === 'localhost' || host === '127.0.0.1';

const resolveApiBaseUrl = () => {
  const configuredUrl = env.REACT_APP_API_URL ?? env.VITE_API_URL;

  if (!configuredUrl) {
    return getDefaultApiUrl();
  }

  if (typeof window === 'undefined') {
    return configuredUrl;
  }

  try {
    const parsedUrl = new URL(configuredUrl, window.location.origin);
    const currentHost = window.location.hostname;

    if (isLoopbackHost(parsedUrl.hostname) && isLoopbackHost(currentHost) && parsedUrl.hostname !== currentHost) {
      parsedUrl.hostname = currentHost;
      return parsedUrl.toString();
    }
  } catch {
    return configuredUrl;
  }

  return configuredUrl;
};

const API_BASE_URL = resolveApiBaseUrl().replace(/\/$/, '');
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();

type RequestOptions = RequestInit & {
  auth?: boolean;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors?: Record<string, string[]>,
    public readonly code?: string
  ) {
    super(message);
  }
}

const unsafeMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const readCookie = (name: string) => {
  if (typeof document === 'undefined') return null;

  return document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=') ?? null;
};

const readLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(key);
};

const clearClientSession = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.removeItem('auth_user');
  window.localStorage.removeItem('current_church_id');
  window.localStorage.removeItem('accessible_churches');
  window.dispatchEvent(new Event('auth:unauthorized'));
};

const ensureCsrfCookie = async () => {
  await fetch(`${API_ORIGIN}/sanctum/csrf-cookie`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
};

export const apiRequest = async <T,>(path: string, options: RequestOptions = {}): Promise<T> => {
  const headers = new Headers(options.headers);
  const method = (options.method ?? 'GET').toUpperCase();
  const churchId = readLocalStorage('current_church_id');

  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'XMLHttpRequest');

  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (unsafeMethods.has(method)) {
    await ensureCsrfCookie();

    const xsrfToken = readCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers.set('X-XSRF-TOKEN', decodeURIComponent(xsrfToken));
    }
  }

  if (churchId && !headers.has('X-Church-Id')) {
    headers.set('X-Church-Id', churchId);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && options.auth !== false) {
      clearClientSession();
    }

    throw new ApiError(
      payload?.message ?? 'Nao foi possivel concluir a operacao.',
      response.status,
      payload?.errors,
      payload?.code
    );
  }

  return payload as T;
};
